<template>
  <div class="min-h-dvh flex flex-col bg-pitch-black text-total-white">
    <TopNavbar />
    <!-- Some Top Navbar -->
    <div class="flex-1 h-[calc(100dvh-120px)]">
      <div class="flex items-center px-4 py-4 border-b border-gray-600">
        <button @click="goBack" class="text-prime-golden mr-2">
          <img src="@/assets/images/left-chevron.svg" class="w-4" alt="Back" />
        </button>
        <h1 class="text-lg font-semibold w-full text-center">System Logs</h1>
      </div>
      <!-- Scrollable messages area -->
      <div class="flex-1 overflow-y-auto py-4 h-[calc(100dvh-120px)]">
        <div
          v-if="isLoading"
          class="text-center py-4 text-gray-400 flex items-center justify-center h-[calc(100vh-120px)]"
        >
          Loading system logs...
        </div>
        <div v-else>
          <template v-if="messages.length">
            <div
              v-for="(msg, index) in sortedMessages"
              :key="msg.id"
              class="border-b border-gray-600 py-2 mb-2 px-4"
            >
              <!-- Date/time stamp at top -->
              <div
                class="text-xs flex items-center justify-between gap-2 text-gray-400 mb-2 pb-4"
              >
                <div class="flex gap-4 items-center">
                  <img src="@/assets/images/ai-icon.svg" alt="AI" class="w-6 h-6" />
                  <span class="text-sm text-gray-400"
                    >#{{ logStore.threadMessages.length - index }}</span
                  >
                </div>
                {{ formatDate(msg.date_time_created) }}
              </div>

              <!-- AI icon + content, rendered as Markdown -->
              <vue3-markdown-it
                class="text-sm text-left text-gray-200 whitespace-pre-line prose prose-slate prose-invert"
                :source="preprocessLogContent(msg.content)"
                :typographer="true"
              />
            </div>
          </template>
          <p v-else class="text-gray-400 text-center mt-4">No system logs found.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useLogStore } from "@/stores/log";
import TopNavbar from "@/components/TopNavbar.vue";
import Vue3MarkdownIt from "vue3-markdown-it";

const route = useRoute();
const router = useRouter();
const logStore = useLogStore();

const threadId = route.params.threadId || route.query.thread_id;
const isLoading = ref(true);
const messages = ref([]);

// On mount, fetch the messages for this agent_system thread
onMounted(async () => {
  try {
    await logStore.fetchMessagesByThreadId(Number(threadId), "asc", 1, 100);
    messages.value = [...logStore.threadMessages];
  } catch (err) {
    console.error("Error fetching system logs:", err);
  } finally {
    isLoading.value = false;
  }
});

// Sort messages by date/time ascending
const sortedMessages = computed(() =>
  [...messages.value].sort(
    (a, b) => new Date(b.date_time_created) - new Date(a.date_time_created)
  )
);
function preprocessLogContent(raw) {
  return (
    raw
      // Convert literal `\n` to real newlines:
      .replace(/\\n/g, "\n")
      // Convert fancy quotes to normal quotes:
      .replace(/[“”]/g, '"')
      // Possibly remove the leading/trailing dash lines if not needed:
      .replace(/^—\s*\n?/, "")
      .replace(/\n?—$/, "")
  );
}
function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function goBack() {
  if (route.query.tab) {
    router.push({
      name: "ProjectPage",
      params: { id: route.query.projectId || route.params.projectId || route.params.id },
      query: { tab: route.query.tab, readLogs: route.query.readLogs },
    });
  } else {
    router.go(-1);
  }
}
</script>

<style scoped>
/* Basic styling */
</style>
